import './productConfiguration.scss';

import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import produce from 'utils/produce';
import classNames from 'classnames';
import { Card, Typography, Alert, Badge, Form } from 'components/base';
import useDataContext from 'data/context/data';
import { formatPrice } from 'utils/format';
import productReducer from 'data/lib/productReducer';
import i18next from 'internationalization';

import {
  getPrimaryProducts,
  getInitialConfiguration,
  getSecondaryProductOptions,
  computePriceData,
} from '../lib/productConfiguration';
import ConfigurationSection from './productConfiguration/configurationSection';
import OptionConfigurator from './productConfiguration/optionConfigurator';

export const prepareBundleData = (dataContext, configuration) => {
  if (configuration.bundle) {
    return {
      bundle: {
        id: configuration.bundle.id,
        name: configuration.bundle.name,
        secondaryProducts: dataContext
          .getSkuProducts(configuration.secondaryProductIds)
          .map(secondaryProduct => ({
            id: secondaryProduct.id,
            name: secondaryProduct.name,
            version: secondaryProduct.version,
            data: configuration.secondaryProductData,
          })),
      },
    };
  }
  if (configuration.upsell) {
    return {
      upsell: {
        id: configuration.upsell.id,
        name: configuration.upsell.name,
        version: configuration.upsell.version,
        data: configuration.secondaryProductData,
      },
    };
  }
  return {};
};

const ProductConfiguration = props => {
  const DEFAULT_SECONDARY_OPTION = props.defaultPrintOnly ? -1 : 0;

  const dataContext = useDataContext();

  // dataContext's identity changes on every provider render, but its SKU data is fixed after mount
  const primaryProducts = useMemo(
    () => getPrimaryProducts(props.product, dataContext),
    [props.product],
  );

  const [configuration, setConfiguration] = useState(() =>
    getInitialConfiguration(props.product, primaryProducts, DEFAULT_SECONDARY_OPTION),
  );

  const secondaryProductOptions = useMemo(
    () => getSecondaryProductOptions(props.product, props.skuProduct, dataContext),
    [props.product, props.skuProduct],
  );
  const upsellOptions = props.skuProduct ? props.product.upsell : [];

  const priceData = useMemo(
    () =>
      computePriceData(
        props.product,
        primaryProducts,
        secondaryProductOptions,
        upsellOptions,
        configuration,
      ),
    [props.product, primaryProducts, secondaryProductOptions, upsellOptions, configuration],
  );
  useEffect(() => {
    props.onChange(priceData);
  }, [priceData]);

  const handleOnChangeConfiguration = (id, value) => {
    if (props.product.reducer) {
      const isProductValid = productReducer(props.product.reducer, props.product.options, {
        ...configuration,
        [id]: value,
      });

      if (!isProductValid) {
        const availableConfig = productReducer(props.product.reducer, props.product.options, {
          ...configuration,
          [id]: value,
          firstAvailable: value,
        });

        if (availableConfig) {
          setConfiguration({
            ...availableConfig,
            secondaryIndex: configuration.secondaryIndex,
          });
        } else {
          alert('Sorry, this variant is not available.');
        }
        return;
      }
    }

    setConfiguration(
      produce(configuration, draft => {
        draft[id] = value;
      }),
    );
  };

  const handleOnChangeOptions = (key, value) => {
    setConfiguration(
      produce(configuration, draft => {
        draft[key] = value;
      }),
    );
  };

  if (!configuration || props.hidden) {
    return null;
  }

  const renderBundleSection = primaryProduct => {
    const renderPrintOnlyCard = (render = false) => {
      if (!render) {
        return null;
      }

      const diffPrice = primaryProduct.price - priceData.price;

      return (
        <Card
          asLink
          onClick={e => {
            e.preventDefault();
            handleOnChangeConfiguration('secondaryIndex', -1);
          }}
          className={classNames({
            active:
              configuration.secondaryIndex === -1 ||
              configuration.secondaryIndex >= secondaryProductOptions.length,
            [props.cardClassName]: props.cardClassName,
          })}
        >
          <div className="card-inline split">
            <div className="h6">{i18next.t(`printsonly.${primaryProduct.id}`, 'Prints Only')}</div>
            <strong className={props.priceLabelClassName}>
              {diffPrice === 0 ? (
                <>&nbsp;</>
              ) : (
                <>
                  {diffPrice > 0 && '+'}
                  {formatPrice(diffPrice, primaryProduct.currency, true)}
                </>
              )}
            </strong>
          </div>
        </Card>
      );
    };

    return (
      <>
        <Typography.Span block className="label text-light">
          {`Select an option`.toUpperCase()}
        </Typography.Span>
        <div
          className="card-list"
          data-items-in-row={Math.min(2, secondaryProductOptions.length + 1)}
        >
          {renderPrintOnlyCard(props.defaultPrintOnly && !props.hidePrintOnly)}

          {secondaryProductOptions.map((p, index) => {
            /**
             * Math.min for minimal discount
             * Math.max for maximum discount
             */
            const savings =
              Math.min(
                ...p.products.secondary
                  .map(secondaryId => dataContext.getPrice(secondaryId))
                  .filter(price => !!price)
                  .map(({ price }) => price),
              ) +
              primaryProduct.price -
              p.price;

            const diffPrice = p.price - priceData.price;

            return (
              <Card
                key={p.id}
                asLink
                onClick={e => {
                  e.preventDefault();
                  handleOnChangeConfiguration('secondaryIndex', index);
                }}
                className={classNames({
                  active: configuration.secondaryIndex === index,
                  [props.cardClassName]: props.cardClassName,
                })}
              >
                <div className="card-inline split">
                  <div className="h6">{p.secondaryCaption}</div>
                  <strong className={props.priceLabelClassName}>
                    {diffPrice === 0 ? (
                      <>&nbsp;</>
                    ) : (
                      <>
                        {diffPrice > 0 && '+'}
                        {formatPrice(diffPrice, p.currency, true)}
                      </>
                    )}
                  </strong>
                </div>
                <Badge type="success" pill className="badge-config-sm">
                  RECOMMENDED{savings > 0 && `. SAVE ${formatPrice(savings, p.currency)}!`}
                </Badge>
              </Card>
            );
          })}

          {renderPrintOnlyCard(!props.defaultPrintOnly && !props.hidePrintOnly)}
        </div>

        <OptionConfigurator
          primaryProduct={primaryProduct}
          products={
            configuration.secondaryIndex >= 0 &&
            secondaryProductOptions.length > configuration.secondaryIndex
              ? dataContext.getSkuProducts(
                  secondaryProductOptions[configuration.secondaryIndex].products.secondary,
                  // { type: 'BUNDLE_SECONDARY_PRODUCTS', primaryId: primaryProduct?.id }, // unused custom transformer
                )
              : []
          }
          minNumberOfItems={props.minNumberOfItems}
          onChange={value => handleOnChangeOptions('secondaryData', value)}
          cardClassName={props.cardClassName}
          labels={props.labels}
        />
      </>
    );
  };

  const renderUpsellSection = primaryProduct => {
    const renderPrintOnlyCard = (render = false) => {
      if (!render) {
        return null;
      }

      const diffPrice = 0;

      return (
        <Card
          asLink
          onClick={e => {
            e.preventDefault();
            handleOnChangeConfiguration('secondaryIndex', -1);
          }}
          className={classNames({
            active:
              configuration.secondaryIndex === -1 ||
              configuration.secondaryIndex >= upsellOptions.length,
            [props.cardClassName]: props.cardClassName,
          })}
        >
          <div className="card-inline split">
            <div className="h6">{i18next.t(`printsonly.${primaryProduct.id}`, 'Prints Only')}</div>
            <strong className={props.priceLabelClassName}>
              {diffPrice === 0 ? (
                <>&nbsp;</>
              ) : (
                <>
                  {diffPrice > 0 && '+'}
                  {formatPrice(diffPrice, primaryProduct.currency, true)}
                  {primaryProduct.priceTiers && ` Per ${props.productType}`}
                </>
              )}
            </strong>
          </div>
        </Card>
      );
    };

    return (
      <>
        <Typography.Span block className="label text-light">
          {`Select an option`.toUpperCase()}
        </Typography.Span>
        <div className="card-list" data-items-in-row={Math.min(2, upsellOptions.length + 1)}>
          {renderPrintOnlyCard(props.defaultPrintOnly && !props.hidePrintOnly)}

          {upsellOptions.map((upsell, index) => {
            const diffPrice = Math.min(...upsell.skuProducts.map(p => p.price));

            return (
              <Card
                key={upsell.label}
                asLink
                onClick={e => {
                  e.preventDefault();
                  handleOnChangeConfiguration('secondaryIndex', index);
                }}
                className={classNames({
                  active: configuration.secondaryIndex === index,
                  [props.cardClassName]: props.cardClassName,
                })}
              >
                <div className="card-inline split">
                  <div className="h6">{upsell.label}</div>
                  <strong className={classNames(props.priceLabelClassName, 'text-right')}>
                    {diffPrice === 0 ? (
                      <>&nbsp;</>
                    ) : (
                      <>
                        {diffPrice > 0 && '+'}
                        {formatPrice(diffPrice, primaryProduct.currency, true)}
                        {primaryProduct.priceTiers && ` Per ${props.productType}`}
                      </>
                    )}
                  </strong>
                </div>
                {upsell.recommended && (
                  <Badge type="success" pill className="badge-config-sm">
                    RECOMMENDED
                  </Badge>
                )}
              </Card>
            );
          })}

          {renderPrintOnlyCard(!props.defaultPrintOnly && !props.hidePrintOnly)}
        </div>

        <OptionConfigurator
          primaryProduct={primaryProduct}
          products={
            configuration.secondaryIndex >= 0 && upsellOptions.length > configuration.secondaryIndex
              ? upsellOptions[configuration.secondaryIndex].skuProducts
              : []
          }
          minNumberOfItems={props.minNumberOfItems}
          onChange={value => handleOnChangeOptions('secondaryData', value)}
          cardClassName={props.cardClassName}
          labels={props.labels}
        />
      </>
    );
  };

  const renderQuantitySection = primaryProduct => {
    if (!primaryProduct.priceTiers) {
      return null;
    }

    const values = Object.keys(primaryProduct.priceTiers);

    return (
      <div className="configuration-row mb-2">
        <Typography.Span block className="label text-light">
          QUANTITY
        </Typography.Span>
        <Form.Select
          onChange={e => handleOnChangeConfiguration('quantity', parseInt(e.target.value, 10))}
        >
          {values.map(option => (
            <Form.SelectOption key={option} value={option}>
              {`${option}  -  ${formatPrice(
                primaryProduct.priceTiers[option],
                primaryProduct.currency,
              )} Per ${props.productType}`}
            </Form.SelectOption>
          ))}
        </Form.Select>
      </div>
    );
  };

  if (props.product.reducer) {
    return (
      <>
        {
          productReducer(props.product.reducer, props.product.options).reduce(
            (acc, { key, label, sort, values, unavailable, infoOnly, availableOnly }) => {
              if (infoOnly || values.length === 1) {
                acc.children.push(
                  <div key={key} className="configuration-cell">
                    <div className="h6">{label.toUpperCase()}</div>
                    <div className="h3 mb-3">{configuration[key]}</div>
                  </div>,
                );
              } else if (values.length > 1) {
                const isOptionDisabled = value => {
                  if (availableOnly) {
                    const val = productReducer(props.product.reducer, props.product.options, {
                      ...acc.selected,
                      [key]: value,
                      isPossible: true,
                    });

                    return !val;
                  }
                  return false;
                };

                acc.children.push(
                  <ConfigurationSection
                    key={key}
                    templateRootPath={props.skuProduct.templateRootPath}
                    id={key}
                    label={`Select ${label}`}
                    options={values
                      .map(value => ({
                        id: value,
                        label: value,
                        disabled: isOptionDisabled(value),
                      }))
                      .sort(({ label: a }, { label: b }) => {
                        if (sort === 'asc') {
                          return a < b ? -1 : 1;
                        }
                        if (sort === 'desc') {
                          return a > b ? -1 : 1;
                        }
                        return 0;
                      })}
                    unavailable={unavailable}
                    selectedOptionId={configuration[key]}
                    onChange={handleOnChangeConfiguration}
                    cardClassName={props.cardClassName}
                    description={props.product.config.variantDescription}
                    autoItemsInRow={props.autoItemsWidth}
                  />,
                );
              }

              acc.selected[key] = configuration[key];
              return acc;
            },
            { selected: {}, children: [] },
          ).children
        }

        {secondaryProductOptions.length > 0 && (
          <div className="configuration-row">{renderBundleSection(props.skuProduct)}</div>
        )}

        {renderQuantitySection(props.skuProduct)}

        {upsellOptions.length > 0 && (
          <div className="configuration-row">{renderUpsellSection(props.skuProduct)}</div>
        )}

        {props.showSkuDescription && props.skuProduct.description && (
          <Alert type="warning">{props.skuProduct.description}</Alert>
        )}
      </>
    );
  }

  const primaryProduct = props.skuProduct;

  if (!primaryProduct) {
    return null;
  }

  return (
    <>
      {primaryProducts.length > 1 && (
        <>
          <ConfigurationSection
            product={props.product}
            id="primaryIndex"
            templateRootPath={primaryProduct.templateRootPath}
            label={primaryProduct.primaryCaption}
            options={primaryProducts.map((p, index) => ({
              id: index,
              label: p.templateMetaData.name,
              description: props.optionDescriptions[p.id],
            }))}
            selectedOptionId={configuration.primaryIndex}
            onChange={handleOnChangeConfiguration}
            cardClassName={props.cardClassName}
            description={props.product.config.variantDescription}
            autoItemsInRow={props.autoItemsWidth}
          />
        </>
      )}

      <OptionConfigurator
        primaryProduct={undefined}
        products={[primaryProduct]}
        onChange={value => handleOnChangeOptions('primaryData', value)}
        cardClassName={props.cardClassName}
        labels={props.labels}
      />

      {secondaryProductOptions.length > 0 && <>{renderBundleSection(primaryProduct)}</>}

      {renderQuantitySection(primaryProduct)}

      {upsellOptions.length > 0 && (
        <div className="configuration-row">{renderUpsellSection(primaryProduct)}</div>
      )}

      {props.showSkuDescription && primaryProduct.description && (
        <Alert type="warning">{primaryProduct.description}</Alert>
      )}
    </>
  );
};

ProductConfiguration.propTypes = {
  product: PropTypes.object,
  skuProduct: PropTypes.object,
  onChange: PropTypes.func,
  cardClassName: PropTypes.string,
  priceLabelClassName: PropTypes.string,
  defaultPrintOnly: PropTypes.bool,
  hidePrintOnly: PropTypes.bool,
  minNumberOfItems: PropTypes.number,
  hidden: PropTypes.bool,
  autoItemsWidth: PropTypes.bool,
  showSkuDescription: PropTypes.bool,
  labels: PropTypes.object,
  optionDescriptions: PropTypes.object,
  productType: PropTypes.string,
};

ProductConfiguration.defaultProps = {
  priceLabelClassName: 'text-info',
  hidden: false,
  defaultPrintOnly: false,
  minNumberOfItems: 2,
  hidePrintOnly: false,
  autoItemsWidth: false,
  showSkuDescription: false,
  labels: {},
  optionDescriptions: {},
  productType: 'Print',
};

export default ProductConfiguration;
